import moment from "moment";

let jsPDF;
try {
	jsPDF = require("jspdf").default || require("jspdf");
} catch (error) {
	console.error("jsPDF non è installato. Esegui: npm install jspdf");
	jsPDF = null;
}

const safeText = (value, fallback = "---") =>
	value === null || value === undefined || value === ""
		? fallback
		: String(value);

export const generateInternalComparisonPDF = (blocks) => {
	if (!jsPDF) {
		throw new Error(
			"jsPDF non è installato. Per utilizzare questa funzionalità, installa jsPDF eseguendo: npm install jspdf",
		);
	}

	// A4 landscape: serve spazio orizzontale
	const doc = new jsPDF({unit: "pt", format: "a4", orientation: "landscape"});
	const pageWidth = doc.internal.pageSize.getWidth();
	const pageHeight = doc.internal.pageSize.getHeight();

	const theme = {
		primary: [16, 89, 191],
		primaryDark: [9, 53, 128],
		primaryLight: [59, 130, 246],
		accent: [28, 193, 255],
		bg: [245, 247, 250],
		border: [229, 231, 235],
		text: [30, 41, 59],
		muted: [100, 116, 139],
		best: [161, 98, 7],
	};

	const SP = {
		pagePad: 36,
		footerH: 44,
		blockGap: 12,
	};

	let y = 0;

	// -------------------------
	// helpers
	// -------------------------
	const setOpacity = (opacity) => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity}));
		} catch (_) {}
	};

	const resetOpacity = () => {
		try {
			if (doc.GState) doc.setGState(new doc.GState({opacity: 1}));
		} catch (_) {}
	};

	const ellipsize = (
		text,
		maxWidth,
		fontSize = 10,
		font = ["helvetica", "normal"],
	) => {
		const s = safeText(text, "");
		if (!s) return "";
		doc.setFont(font[0], font[1]);
		doc.setFontSize(fontSize);
		if (doc.getTextWidth(s) <= maxWidth) return s;

		const ell = "…";
		let lo = 0;
		let hi = s.length;
		while (lo < hi) {
			const mid = Math.floor((lo + hi) / 2);
			const candidate = s.slice(0, mid) + ell;
			if (doc.getTextWidth(candidate) <= maxWidth) lo = mid + 1;
			else hi = mid;
		}
		return s.slice(0, Math.max(0, lo - 1)) + ell;
	};

	const drawCard = (
		x,
		y,
		w,
		h,
		{radius = 12, shadow = true, fill = [255, 255, 255]} = {},
	) => {
		if (shadow) {
			doc.setFillColor(0, 0, 0);
			setOpacity(0.06);
			doc.roundedRect(x + 2, y + 3, w, h, radius, radius, "F");
			resetOpacity();
		}
		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);
		doc.setFillColor(...fill);
		doc.roundedRect(x, y, w, h, radius, radius, "FD");
	};

	const drawHeader = () => {
		const headerH = 74;

		doc.setFillColor(...theme.primaryDark);
		doc.rect(0, 0, pageWidth, headerH, "F");

		doc.setFillColor(...theme.primary);
		doc.rect(0, headerH - 18, pageWidth, 18, "F");

		doc.setFillColor(...theme.accent);
		doc.rect(0, headerH - 3, pageWidth, 3, "F");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(18);
		doc.setTextColor(255, 255, 255);
		doc.text("Confronto Interni vs Esterni", SP.pagePad, 36);

		doc.setFont("helvetica", "normal");
		doc.setFontSize(10);
		doc.setTextColor(235, 245, 255);
		doc.text(
			`Generato il ${moment().format("DD/MM/YYYY • HH:mm")}`,
			SP.pagePad,
			54,
		);

		setOpacity(0.06);
		doc.setFont("helvetica", "bold");
		doc.setFontSize(36);
		doc.setTextColor(255, 255, 255);
		doc.text("COMPARISON", pageWidth - SP.pagePad, 48, {align: "right"});
		resetOpacity();

		doc.setTextColor(...theme.text);
		y = headerH + 16;
	};

	const drawFooterAllPages = () => {
		const totalPages = doc.internal.pages.length - 1;
		const generatedAt = `Generato il ${moment().format("DD/MM/YYYY • HH:mm")}`;

		for (let i = 1; i <= totalPages; i++) {
			doc.setPage(i);

			doc.setDrawColor(...theme.border);
			doc.setLineWidth(1);
			doc.line(
				SP.pagePad,
				pageHeight - SP.footerH + 16,
				pageWidth - SP.pagePad,
				pageHeight - SP.footerH + 16,
			);

			doc.setFont("helvetica", "normal");
			doc.setFontSize(9);
			doc.setTextColor(...theme.muted);

			doc.text(generatedAt, SP.pagePad, pageHeight - 16);
			doc.text(
				`Pagina ${i} / ${totalPages}`,
				pageWidth - SP.pagePad,
				pageHeight - 16,
				{align: "right"},
			);
		}

		doc.setTextColor(...theme.text);
	};

	const ensureSpace = (needed) => {
		if (y + needed > pageHeight - SP.footerH) {
			doc.addPage();
			drawHeader();
			return true;
		}
		return false;
	};

	// -------------------------
	// Columns (landscape)
	// -------------------------
	const availableW = pageWidth - SP.pagePad * 2;

	const columns = [
		{key: "best", label: "", width: 48, align: "center"},
		{key: "player", label: "Giocatore", width: 220, align: "left"},
		{key: "role", label: "Ruolo", width: 70, align: "center"},
		{key: "rating", label: "Rating", width: 70, align: "center"},
		// colonna più grande, ma TESTO SEMPRE SU 1 RIGA (ellipsis)
		{
			key: "external",
			label: "Esterni (top 3)",
			width: Math.max(260, availableW - (48 + 220 + 70 + 70 + 80)),
			align: "left",
		},
		{key: "delta", label: "Diff.", width: 80, align: "center"},
	];

	// force sum widths = availableW
	const sumW = columns.reduce((s, c) => s + c.width, 0);
	if (sumW !== availableW) {
		const external = columns.find((c) => c.key === "external");
		external.width += availableW - sumW;
	}

	const tableX = SP.pagePad;

	const drawYearTitle = (birthYear) => {
		ensureSpace(44);

		const label = `Anno ${safeText(birthYear, "")}`.trim() || "Anno";

		doc.setFillColor(...theme.primary);
		doc.roundedRect(SP.pagePad, y, pageWidth - SP.pagePad * 2, 22, 10, 10, "F");

		doc.setFillColor(...theme.accent);
		doc.circle(SP.pagePad + 12, y + 11, 4, "F");

		doc.setFont("helvetica", "bold");
		doc.setFontSize(12);
		doc.setTextColor(255, 255, 255);
		doc.text(label, SP.pagePad + 22, y + 15);

		doc.setTextColor(...theme.text);
		y += 22 + 10;
	};

	const drawTableHeader = () => {
		const headerH = 28;

		drawCard(tableX, y, availableW, headerH, {
			radius: 10,
			shadow: true,
			fill: theme.bg,
		});

		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);

		doc.setFont("helvetica", "bold");
		doc.setFontSize(10);
		doc.setTextColor(...theme.text);

		let x = tableX;
		columns.forEach((col, idx) => {
			const tx = col.align === "center" ? x + col.width / 2 : x + 10;
			if (col.label) doc.text(col.label, tx, y + 18, {align: col.align});

			if (idx > 0) doc.line(x, y + 6, x, y + headerH - 6);
			x += col.width;
		});

		y += headerH + 8;
	};

	const formatNum = (v) =>
		v !== null && v !== undefined && !Number.isNaN(v)
			? Number(v).toFixed(2)
			: "---";

	const formatDelta = (v) => {
		if (v === null || v === undefined || Number.isNaN(v)) return "---";
		const n = Number(v);
		return `${n > 0 ? "+" : ""}${n.toFixed(2)}`;
	};

	const buildExternalTop = (row) =>
		(row?.externalTopList || [])
			.slice(0, 3)
			.map((entry) => {
				const val =
					entry?.avg !== null && entry?.avg !== undefined
						? Number(entry.avg).toFixed(2)
						: "---";
				return `${safeText(entry?.name, "---")} (${val})`;
			})
			.join(", ");

	// -------------------------
	// FIX: righe a altezza fissa
	// -------------------------
	const ROW_H = 28; // fissa => niente page con 1 giocatore
	const BASELINE_Y = 18; // baseline testo nella riga
	const PAD_X = 10;

	const drawRow = (row, {isBest = false, zebra = false} = {}) => {
		ensureSpace(ROW_H + 6);

		if (zebra) {
			doc.setFillColor(...theme.bg);
			doc.roundedRect(tableX, y, availableW, ROW_H, 10, 10, "F");
		}

		doc.setDrawColor(...theme.border);
		doc.setLineWidth(0.8);
		doc.roundedRect(tableX, y, availableW, ROW_H, 10, 10, "S");

		const playerName =
			`${safeText(row?.player?.firstName, "")} ${safeText(row?.player?.lastName, "")}`.trim() ||
			"---";
		const role = safeText(row?.roleKey, "---");
		const rating = formatNum(row?.internalAvg);
		const externalTop = buildExternalTop(row) || "---";
		const delta = formatDelta(row?.delta);

		let x = tableX;

		// BEST badge
		if (isBest) {
			doc.setFillColor(...theme.best);
			doc.roundedRect(x + 10, y + 7, 28, 14, 6, 6, "F");
			doc.setFont("helvetica", "bold");
			doc.setFontSize(8);
			doc.setTextColor(255, 255, 255);
			doc.text("BEST", x + 24, y + 17, {align: "center"});
		}
		x += columns[0].width;

		// Player (1 riga ellipsized)
		const playerMaxW = columns[1].width - PAD_X * 2;
		doc.setFont("helvetica", "normal");
		doc.setFontSize(10);
		doc.setTextColor(...theme.text);
		doc.text(ellipsize(playerName, playerMaxW, 10), x + PAD_X, y + BASELINE_Y);
		x += columns[1].width;

		// Role
		doc.text(role, x + columns[2].width / 2, y + BASELINE_Y, {align: "center"});
		x += columns[2].width;

		// Rating
		doc.text(rating, x + columns[3].width / 2, y + BASELINE_Y, {
			align: "center",
		});
		x += columns[3].width;

		// External (1 riga ellipsized + colore muted)
		const externalMaxW = columns[4].width - PAD_X * 2;
		doc.setFontSize(9);
		doc.setTextColor(...theme.muted);
		const externalOneLine = ellipsize(externalTop, externalMaxW, 9, [
			"helvetica",
			"normal",
		]);
		doc.text(externalOneLine, x + PAD_X, y + BASELINE_Y);
		doc.setTextColor(...theme.text);
		x += columns[4].width;

		// Delta (bold + color)
		doc.setFont("helvetica", "bold");
		doc.setFontSize(10);
		const deltaColor =
			row?.delta !== null && row?.delta !== undefined
				? Number(row.delta) >= 0
					? theme.primary
					: theme.best
				: theme.text;
		doc.setTextColor(...deltaColor);
		doc.text(delta, x + columns[5].width / 2, y + BASELINE_Y, {
			align: "center",
		});

		// reset
		doc.setTextColor(...theme.text);
		doc.setFont("helvetica", "normal");

		y += ROW_H + 6;
	};

	// =========================
	// Render
	// =========================
	drawHeader();

	(blocks || []).forEach((block) => {
		drawYearTitle(block?.birthYear);
		drawTableHeader();

		(block?.rows || []).forEach((row, idx) => {
			const isBest =
				block?.bestByRole?.[row?.roleKey]?.player?.id === row?.player?.id;
			drawRow(row, {isBest, zebra: idx % 2 === 1});
		});

		y += 6;
	});

	drawFooterAllPages();

	const fileName = `confronto_interni_${moment().format("YYYYMMDD_HHmm")}.pdf`;
	doc.save(fileName);
};
